import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, UserPlus, Brain, Sparkles,
  Trash2, AlertCircle, Search, CheckCircle2, XCircle,
  Calendar, Eye, Network
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserPartner } from '../../lib/auth';
import {
  getPartnerIdForUser,
  loadTeamMembers,
  loadAvailableRespondents,
  loadTeamResults,
  computeTeamAverages,
} from '../../lib/teams';
import { ResultsGraph } from '../../components/ResultsGraph';
import { HarmonicGraph } from '../../components/HarmonicGraph';
import { BarometerGauge } from '../../components/BarometerGauge';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  computeBarometers,
  getPotentialLabel,
  getMoodLabel,
  getBlockerLabel,
} from '../../lib/barometers';
import type { TeamMember, TeamAverageScores } from '../../types/team';

export function PartnerTeamDetail() {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const { user, isLoading: authLoading } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [teamType, setTeamType] = useState('');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [averages, setAverages] = useState<TeamAverageScores | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [availableRespondents, setAvailableRespondents] = useState<{ userId: string; name: string; email: string }[]>([]);
  const [selectedRespondents, setSelectedRespondents] = useState<Set<string>>(new Set());
  const [addSearch, setAddSearch] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const [activeTab, setActiveTab] = useState<'averages' | 'members'>('averages');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !teamId) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isPartner = await isUserPartner(user.id);
        if (!isPartner) {
          navigate('/login');
          return;
        }

        const pId = await getPartnerIdForUser(user.id);
        if (!pId) {
          navigate('/login');
          return;
        }
        setPartnerId(pId);

        const { data: team } = await supabase
          .from('teams')
          .select('name, description, type, partner_id')
          .eq('id', teamId)
          .maybeSingle();

        if (!team || team.partner_id !== pId) {
          navigate('/partner/teams');
          return;
        }

        setTeamName(team.name);
        setTeamDescription(team.description || '');
        setTeamType(team.type);

        await refreshData();
      } catch (err) {
        console.error('Error initializing:', err);
        navigate('/partner/teams');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate, teamId]);

  const refreshData = async () => {
    if (!teamId) return;

    try {
      const loadedMembers = await loadTeamMembers(teamId);
      setMembers(loadedMembers);

      const resultsData = await loadTeamResults(teamId);
      const computed = computeTeamAverages(teamId, teamName, resultsData);
      computed.memberCount = loadedMembers.length;
      setAverages(computed);
    } catch (err) {
      console.error('Error loading team data:', err);
      setError('Failed to load team data');
    }
  };

  const openAddMembersModal = async () => {
    if (!partnerId || !teamId) return;

    try {
      const available = await loadAvailableRespondents(partnerId, teamId);
      setAvailableRespondents(available);
      setSelectedRespondents(new Set());
      setAddSearch('');
      setShowAddModal(true);
    } catch (err) {
      console.error('Error loading available respondents:', err);
      setError('Failed to load available respondents');
    }
  };

  const handleAddMembers = async () => {
    if (!teamId || selectedRespondents.size === 0) return;

    setIsAdding(true);
    try {
      const inserts = Array.from(selectedRespondents).map(userId => ({
        team_id: teamId,
        user_id: userId,
        role: 'member' as const,
      }));

      const { error: insertError } = await supabase
        .from('team_members')
        .insert(inserts);

      if (insertError) throw insertError;

      setShowAddModal(false);
      await refreshData();
    } catch (err: any) {
      console.error('Error adding members:', err);
      setError(err.message || 'Failed to add members');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!confirm(`Remove ${memberName} from this team?`)) return;

    try {
      const { error: deleteError } = await supabase
        .from('team_members')
        .delete()
        .eq('id', memberId);

      if (deleteError) throw deleteError;

      await refreshData();
    } catch (err: any) {
      console.error('Error removing member:', err);
      setError(err.message || 'Failed to remove member');
    }
  };

  const filteredAvailable = availableRespondents.filter(r =>
    r.name.toLowerCase().includes(addSearch.toLowerCase()) ||
    r.email.toLowerCase().includes(addSearch.toLowerCase())
  );

  const toggleRespondent = (userId: string) => {
    setSelectedRespondents(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading team details..." />;
  }

  const membersWithResults = members.filter(m => m.hasResults).length;
  const completionRate = members.length > 0
    ? Math.round((membersWithResults / members.length) * 100)
    : 0;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link
            to="/partner/teams"
            className="flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Teams
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl shadow-soft ${teamType === 'department' ? 'bg-warm-500' : 'bg-sage-500'}`}>
                {teamType === 'department'
                  ? <Network className="w-6 h-6 text-white" />
                  : <Users className="w-6 h-6 text-white" />
                }
              </div>
              <div>
                <h1 className="text-2xl font-bold text-chocolate-900">{teamName}</h1>
                {teamDescription && (
                  <p className="text-sm text-chocolate-500">{teamDescription}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to={`/partner/teams/${teamId}/insights`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium bg-warm-500 text-white hover:bg-warm-600 transition-colors"
              >
                <Sparkles className="w-5 h-5" />
                Team Insights
              </Link>
              <button
                onClick={openAddMembersModal}
                className="freud-btn-primary flex items-center gap-2"
              >
                <UserPlus className="w-5 h-5" />
                Add Members
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="freud-card-elevated p-6"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-3xl font-bold text-chocolate-900">{members.length}</div>
                <div className="text-sm text-chocolate-500">Total Members</div>
              </div>
              <div className="p-3 bg-sage-500 rounded-xl shadow-soft">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="freud-card-elevated p-6"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-3xl font-bold text-chocolate-900">{membersWithResults}</div>
                <div className="text-sm text-chocolate-500">Completed Assessments</div>
              </div>
              <div className="p-3 bg-warm-500 rounded-xl shadow-soft">
                <Brain className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="freud-card-elevated p-6"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-3xl font-bold text-chocolate-900">{completionRate}%</div>
                <div className="text-sm text-chocolate-500">Completion Rate</div>
              </div>
              <div className="p-3 bg-coral-500 rounded-xl shadow-soft">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mt-3 h-2 bg-cream-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-coral-500 rounded-full transition-all"
                style={{ width: `${completionRate}%` }}
              />
            </div>
          </motion.div>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('averages')}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === 'averages'
                ? 'bg-chocolate-800 text-white'
                : 'bg-cream-200 text-chocolate-700 hover:bg-cream-300'
            }`}
          >
            Team Averages
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === 'members'
                ? 'bg-chocolate-800 text-white'
                : 'bg-cream-200 text-chocolate-700 hover:bg-cream-300'
            }`}
          >
            Members ({members.length})
          </button>
        </div>

        {activeTab === 'averages' && (
          <TeamAveragesView averages={averages} />
        )}

        {activeTab === 'members' && (
          <MembersTable
            members={members}
            onRemove={handleRemoveMember}
            onViewResults={(userId) => navigate(`/partner/teams/${teamId}/results/${userId}`)}
          />
        )}
      </main>

      {showAddModal && (
        <AddMembersModal
          available={filteredAvailable}
          selected={selectedRespondents}
          search={addSearch}
          onSearchChange={setAddSearch}
          onToggle={toggleRespondent}
          onAdd={handleAddMembers}
          onClose={() => setShowAddModal(false)}
          isAdding={isAdding}
        />
      )}
    </div>
  );
}

function TeamAveragesView({ averages }: { averages: TeamAverageScores | null }) {
  if (!averages || averages.membersWithResults === 0) {
    return (
      <div className="freud-card-elevated p-12 text-center">
        <Brain className="w-16 h-16 text-cream-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-chocolate-700 mb-2">No Results Yet</h3>
        <p className="text-chocolate-500">
          Team averages will appear once members have completed their assessments.
        </p>
      </div>
    );
  }

  const barometers = computeBarometers(averages);
  const potentialLabel = getPotentialLabel(barometers.potentialExpressed);
  const moodLabel = getMoodLabel(barometers.teamMood);
  const blockerLabel = getBlockerLabel(barometers.blockerIndex);

  return (
    <div className="space-y-6">
      <div className="freud-card-elevated p-4 text-center">
        <p className="text-sm text-chocolate-500">
          Averages based on <span className="font-semibold text-chocolate-700">{averages.membersWithResults}</span> of {averages.memberCount} members with completed assessments
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="freud-card-elevated p-6 flex items-center justify-center"
        >
          <BarometerGauge
            value={barometers.potentialExpressed}
            label="Potential Expressed"
            thresholdLabel={potentialLabel.label}
            thresholdColor={potentialLabel.color}
            strokeColor="#6B9B5A"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="freud-card-elevated p-6 flex items-center justify-center"
        >
          <BarometerGauge
            value={barometers.teamMood}
            label="Team Mood"
            thresholdLabel={moodLabel.label}
            thresholdColor={moodLabel.color}
            strokeColor="#B8936A"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="freud-card-elevated p-6 flex items-center justify-center"
        >
          <BarometerGauge
            value={barometers.blockerIndex}
            label="Blocker Index"
            thresholdLabel={blockerLabel.label}
            thresholdColor={blockerLabel.color}
            strokeColor="#E07A5F"
          />
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="freud-card-elevated p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-sage-600" />
            </div>
            <h2 className="text-xl font-bold text-chocolate-900">
              Average Characteristic Profile
            </h2>
          </div>
          <ResultsGraph scores={averages.averageScores} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="freud-card-elevated p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-coral-100 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-coral-600" />
            </div>
            <h2 className="text-xl font-bold text-chocolate-900">
              Average Harmonic Scale Profile
            </h2>
          </div>
          <HarmonicGraph scores={averages.averageHarmonicScores} />
        </motion.div>
      </div>
    </div>
  );
}

function MembersTable({
  members,
  onRemove,
  onViewResults,
}: {
  members: TeamMember[];
  onRemove: (id: string, name: string) => void;
  onViewResults: (userId: string) => void;
}) {
  return (
    <div className="freud-card-elevated">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-cream-100">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Member</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Email</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Coach</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Assessment</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Last Completed</th>
              <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream-200">
            {members.map(member => (
              <tr key={member.id} className="hover:bg-cream-50">
                <td className="px-6 py-4">
                  <span className="font-medium text-chocolate-900">{member.userName}</span>
                </td>
                <td className="px-6 py-4 text-chocolate-600">{member.userEmail}</td>
                <td className="px-6 py-4 text-chocolate-600">{member.coachName}</td>
                <td className="px-6 py-4">
                  {member.hasResults ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-green-700">
                      <CheckCircle2 className="w-4 h-4" />
                      Completed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm text-chocolate-400">
                      <XCircle className="w-4 h-4" />
                      Pending
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {member.lastAssessmentDate ? (
                    <div className="flex items-center gap-1.5 text-sm text-chocolate-600">
                      <Calendar className="w-4 h-4" />
                      {member.lastAssessmentDate.toLocaleDateString()}
                    </div>
                  ) : (
                    <span className="text-sm text-chocolate-400">--</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {member.hasResults && (
                      <button
                        onClick={() => onViewResults(member.userId)}
                        className="p-2 text-sage-600 hover:bg-sage-50 rounded-lg transition-colors"
                        title="View results"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onRemove(member.id, member.userName || '')}
                      className="p-2 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
                      title="Remove from team"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {members.length === 0 && (
        <div className="text-center py-12 text-chocolate-400">
          No members in this team yet. Click "Add Members" to get started.
        </div>
      )}
    </div>
  );
}

function AddMembersModal({
  available,
  selected,
  search,
  onSearchChange,
  onToggle,
  onAdd,
  onClose,
  isAdding,
}: {
  available: { userId: string; name: string; email: string }[];
  selected: Set<string>;
  search: string;
  onSearchChange: (v: string) => void;
  onToggle: (userId: string) => void;
  onAdd: () => void;
  onClose: () => void;
  isAdding: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="freud-card-elevated p-6 w-full max-w-lg m-4 max-h-[80vh] flex flex-col"
      >
        <h2 className="text-xl font-bold text-chocolate-900 mb-4">Add Team Members</h2>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
          <input
            type="text"
            placeholder="Search respondents..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="freud-input pl-10"
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 border border-cream-200 rounded-xl divide-y divide-cream-200">
          {available.length === 0 ? (
            <div className="text-center py-8 text-chocolate-400">
              No available respondents to add
            </div>
          ) : (
            available.map(r => (
              <label
                key={r.userId}
                className="flex items-center gap-3 px-4 py-3 hover:bg-cream-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selected.has(r.userId)}
                  onChange={() => onToggle(r.userId)}
                  className="w-4 h-4 text-sage-600 rounded border-cream-300 focus:ring-sage-500"
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-chocolate-900 text-sm">{r.name}</div>
                  <div className="text-xs text-chocolate-500 truncate">{r.email}</div>
                </div>
              </label>
            ))
          )}
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-cream-200">
          <span className="text-sm text-chocolate-500">{selected.size} selected</span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="freud-btn-secondary"
              disabled={isAdding}
            >
              Cancel
            </button>
            <button
              onClick={onAdd}
              disabled={selected.size === 0 || isAdding}
              className="freud-btn-primary disabled:opacity-50"
            >
              {isAdding ? 'Adding...' : `Add ${selected.size} Member${selected.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
